// File: src/lib/services/settings.ts
import api from '../axios';
import axios from 'axios';

export interface UserAccessSettings {
  roleManagement: {
    storeDirectorAccess: boolean;
    storeLeaderAccess: boolean;
    fohLeaderAccess: boolean;
    bohLeaderAccess: boolean;
    dtLeaderAccess: boolean;
  };
  evaluation: {
    departmentRestriction: boolean;
    requireStoreLeaderReview: boolean;
    requireDirectorApproval: boolean;
    leadersCanManageEvaluations: boolean;
    workflowType: 'simple' | 'standard' | 'strict';
  };
  setupView?: {
    leadersOnly: boolean;
  };
  leadership?: {
    developmentPlansDirectorOnly: boolean;
  };
  documentation?: {
    historyAdminOnly?: boolean;
    receiptOnlyAcknowledgment?: boolean;
    // Display name for improvement plans; see lib/improvementPlanTerm.ts
    improvementPlanTerm?: 'PIP' | 'DAP';
    // Granular acknowledgment policy; resolved with the legacy flag by
    // hooks/useAcknowledgmentPolicy.ts.
    acknowledgment?: {
      askRating?: boolean;
      askComments?: boolean;
      requireSignature?: boolean;
      allowInPersonSigning?: boolean;
    };
  };
  // One person sending one other person a single task. Off until a store opts
  // in. whoCanParticipate governs both ends — who may send AND who may be sent
  // to — which is why it isn't called whoCanSend.
  personalTasks?: {
    enabled?: boolean;
    whoCanParticipate?: PersonalTaskPolicy;
    emailOnAssign?: boolean;
  };
  // Cross-store borrow/loan log. Off until a store opts in.
  borrowLog?: {
    enabled?: boolean;
  };
  // Late time-off requests (inside the request window). Off until a store
  // opts in. recipients get each request and, with admins, answer them.
  lateTimeOff?: {
    enabled?: boolean;
    windowDays?: number;
    recipients?: string[];
  };
  // Auto-fill setup sheets from the HotSchedules schedule. Off until a store opts in.
  setupAutoFill?: {
    enabled?: boolean;
  };
  // ServSafe / food-handler / allergen certificate tracking. Off until a store
  // opts in. See server/src/utils/servSafe.js for the defaults.
  servSafe?: {
    enabled?: boolean;
    minLevel?: number;
    reminderOffsets?: number[];
    weeklyAfterExpiry?: boolean;
    notifyPerson?: boolean;
    notifyAdmins?: boolean;
    notifyStoreEmail?: boolean;
    requireReview?: boolean;
  };
  // RSA/FSA self-assessment report-outs. Off until a store opts in.
  selfAssessments?: {
    enabled?: boolean;
  };
  // Coaching-log reach: off = leaders (level 3+) only; on = trainers too.
  // trainersCanCoachLeaders (needs trainersCanCoach): trainers may also log
  // about people at or above them, seeing only their own notes there.
  coaching?: {
    trainersCanCoach?: boolean;
    trainersCanCoachLeaders?: boolean;
    directorsManageOwnOversight?: boolean;
  };
  // Training plan create/edit/delete: off = leaders (level 3+) too; on = directors only.
  training?: {
    planEditingDirectorOnly?: boolean;
  };
}

export type PersonalTaskPolicy = 'anyone' | 'leadersReceiveOnly' | 'leadersOnly';

export interface EvaluationSchedulingSettings {
  autoSchedule: boolean;
  frequency?: number;
  cycleStart?: 'hire_date' | 'last_evaluation' | 'calendar_year' | 'fiscal_year' | 'custom';
  transitionMode?: 'immediate' | 'complete_cycle' | 'align_next';
  customStartDate?: string;
}

// Opt-in per store, both OFF by default (server: utils/evaluationModeSettings.js).
export interface EvaluationMonthlyWindowsSettings {
  enabled?: boolean;
  firstEvalDays?: number;
  firstEvalTemplate?: string | null;
  positionTemplates?: { position: string; template: string }[];
}

export interface EvaluationMultiReviewerSettings {
  enabled?: boolean;
  minLevel?: number;
}

// Opt-in per store, OFF by default (server: services/evaluationMilestoneScheduler.js).
export interface EvaluationNewHireMilestonesSettings {
  enabled?: boolean;
  milestones?: { days: number; template?: string | null }[];
  appliesFrom?: string | null;
  leadDays?: 0 | 3 | 7;
}

export interface EvaluationSettings {
  scheduling?: EvaluationSchedulingSettings;
  monthlyWindows?: EvaluationMonthlyWindowsSettings;
  multiReviewer?: EvaluationMultiReviewerSettings;
  newHireMilestones?: EvaluationNewHireMilestonesSettings;
  configurationIssues?: {
    unassignedEvaluators: number;
  };
}

export interface MobileNavigationItem {
  key: string;
  show: boolean;
}

export interface UIPreferences {
  mobileNavigation: {
    items: MobileNavigationItem[];
    maxItems: number;
  };
}

export interface StoreUser {
  _id: string;
  name: string;
  email: string;
  position: string;
  role: string;
}

export interface NotificationSettings {
  documentation: {
    emailOnCreation: boolean;
    emailOnAcknowledgment: boolean;
    emailOnAdministrativeCreation: boolean;
    administrativeNotificationLevel: 3 | 4; // 3 = Leaders and up, 4 = Directors and up
    restrictNonCallOutToDirectors: boolean; // When true, only Call Out notifications go to all leaders; all other types go to Directors only
    callOutAttentionEnabled?: boolean; // Flag/alert when an employee calls out too many times
    callOutAttentionThreshold?: number; // Number of call-outs within the window to trigger attention
    callOutAttentionWindowDays?: number; // Rolling window (days) for counting call-outs
  };
  kitchen: {
    emailOnEquipmentBroken: boolean;
    notificationRecipients: string[]; // Array of user IDs
  };
  customerComplaints: {
    alertOnMultipleComplaints: boolean;
    multipleComplaintThreshold: number;
    multipleComplaintTimeWindow: number;
    alertRecipients: string[]; // Array of user IDs
  };
  disciplinary: {
    emailOnCreation: boolean;
    notificationRecipients: string[]; // Array of user IDs
  };
  training?: {
    emailOnTaskComment: boolean;
    notificationRecipients: string[]; // Array of user IDs
  };
}

export interface WhyStatement {
  purpose: string;
  mission: string;
  values: string[];
  strategy: string;
  showOnDashboard?: boolean;
}

export interface ChecklistSettings {
  enableBfTransition?: boolean;
  disableTransitionShift?: boolean;
  // Store-defined extra kitchen shifts. Omit `id` for a new row; the server mints it.
  customShifts?: { id?: string; name: string; startTime: string; endTime?: string; order?: number }[];
  // Store-defined extra FOH task-board shifts; same shape, separate list.
  fohCustomShifts?: { id?: string; name: string; startTime: string; endTime?: string; order?: number }[];
  fohCoreShiftNames?: { opening?: string; transition?: string; closing?: string };
  // Director Board shifts; same shape, third list.
  directorCustomShifts?: { id?: string; name: string; startTime: string; endTime?: string; order?: number }[];
  directorCoreShiftNames?: { opening?: string; transition?: string; closing?: string };
  endOfDayTime?: string;
  breakEligibilityHours?: number;
  breakDurationOptions?: number[];
  shiftTimes?: {
    openingStart?: string;
    openingEnd?: string;
    transitionStart?: string;
    transitionEnd?: string;
    closingStart?: string;
    closingEnd?: string;
  };
}

export interface Infraction {
  name: string;
  // 0 = documented with no points (e.g. a verbal warning)
  points: number;
  category: string;
  // Leader picks 1–10 points when writing it up; `points` is ignored
  variable?: boolean;
}

export interface ConsequenceTier {
  points: number;
  action: string;
}

export interface DocumentationConfig {
  scoringMode: 'standard' | 'points';
  infractions: Infraction[];
  consequences: ConsequenceTier[];
  // Minimum points for an employee to appear on the analytics "Attention Needed" list (point-system stores only)
  attentionThresholdPoints?: number;
  // Rolling window (days) for computing active points, consequence tiers, and warnings (point-system stores only).
  // Store POLICY window — independent of the per-user date-filter view preference. Defaults to 90.
  pointsRollingWindowDays?: number;
  // When true, level-3+ leaders may document same-level peers
  allowPeerDocumentation?: boolean;
  // When true, level-3+ leaders may document people above their level (e.g. a Leader documenting a Director)
  allowUpwardDocumentation?: boolean;
  // When true, every new record is saved with notifyEmployee=true and the write-up form hides the checkbox
  alwaysNotifyEmployee?: boolean;
  // Offer "Injury Report" as an Administrative document type (off by default)
  injuryReportsEnabled?: boolean;
  // Director Board review: flag the first write-up after someone is already at/over the threshold (off by default)
  boardReviewEnabled?: boolean;
  boardReviewThresholdPoints?: number;
  // Position level 3+; null/undefined = same as team members
  boardReviewLeaderThresholdPoints?: number | null;
}

export interface AreaLabelOverride {
  id: string;
  label: string;
}

export interface AreaDefaultMetricOverride {
  id: string;
  name: string;
  target: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  unit?: string;
  lowerIsBetter?: boolean;
}

export interface AreaDefaultTaskOverride {
  id: string;
  title: string;
  enabled: boolean;
  frequency: 'daily' | 'm-f' | 'weekly' | 'monthly';
}

// Per-area store-default metrics/tasks that seed the leader setup wizard.
export interface AreaDefaultOverride {
  id: string;                          // area id, same key space as areaLabels
  metrics: AreaDefaultMetricOverride[];
  tasks: AreaDefaultTaskOverride[];
}

export interface AreaManagementSettings {
  areaLabels: AreaLabelOverride[];
  areaDefaults?: AreaDefaultOverride[];
}

export interface StoreSettings {
  darkMode: boolean;
  compactMode: boolean;
  language: 'en' | 'es';
  storeName: string;
  storeNumber: string;
  storeAddress: string;
  storePhone: string;
  storeEmail: string;
  visionStatement: string;
  missionStatement: string;
  whyStatement?: WhyStatement;
  userAccess: UserAccessSettings;
  evaluations?: EvaluationSettings;
  uiPreferences?: UIPreferences;
  notificationSettings?: NotificationSettings;
  checklists?: ChecklistSettings;
  documentationConfig?: DocumentationConfig;
  areaManagement?: AreaManagementSettings;
  rewardsConfig?: {
    enabled: boolean;
    /** What this store calls its reward points ("CFAdollars" unless renamed). */
    currencyName?: string;
    /** Renames/hides of the built-in catalog categories (lib/rewardCategories). */
    categoryOverrides?: { key: string; label?: string; hidden?: boolean }[];
    /** The store's own catalog categories. */
    customCategories?: { key: string; label: string }[];
    /** Standard "what they did → points" list for the award dialog (lib/rewardAwardPresets). */
    awardPresets?: { label: string; amount: number }[];
  };
  safeConfig?: {
    enabled: boolean;
  };
  fohFoodSafety?: {
    enabled: boolean;
  };
  // Admins can hide the equipment pages; on unless explicitly false (lib/equipmentPages.ts)
  kitchenEquipment?: {
    enabled: boolean;
  };
  fohEquipment?: {
    enabled: boolean;
  };
  directorBoard?: { enabled?: boolean; requireInitials?: boolean };
  shiftReports?: { enabled?: boolean };
  operationalAlerts?: OperationalAlertsSettings;
  displaySettings?: {
    timezone?: string;
  };
  // Optional feature access controls
  // userAccess.setupView.leadersOnly controls Setup View visibility
}

export interface OperationalAlertsShift {
  recipients: string[];
  deadline: string;
  notifyAt: string;
}

export interface OperationalAlertsSettings {
  enabled: boolean;
  notifyOnComplete: boolean;
  notifyOnMissed: boolean;
  shifts: {
    opening: OperationalAlertsShift;
    bf_transition: OperationalAlertsShift;
    transition: OperationalAlertsShift;
    closing: OperationalAlertsShift;
    daily: OperationalAlertsShift;
  };
}

// The settings PATCH endpoint accepts partial, nested updates (e.g. just
// { checklists: { enableBfTransition } } or { userAccess: { setupView: {...} } }),
// so updates are typed as a recursive partial of StoreSettings.
type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;
export type StoreSettingsUpdate = DeepPartial<StoreSettings>;

export interface StoreInfo {
  name: string;
  storeNumber: string;
  storeAddress: string;
  storePhone: string;
  storeEmail: string;
  visionStatement: string;
  missionStatement: string;
  whyStatement?: WhyStatement;
}

export interface SchedulingResults {
  scheduled: number;
  skipped: number;
  errors: number;
}

export interface WasteItem {
  _id?: string;
  name: string;
  unit: string;
  defaultCost: number;
  icon: string;
  backgroundColor?: string; // Hex color code for button background
  mealPeriod: 'breakfast' | 'lunch' | 'dinner';
  isCustom?: boolean;
}

export interface SettingsUpdateResponse extends StoreSettings {
  schedulingResults?: SchedulingResults;
}

export const settingsService = {
  getSettings: async (): Promise<StoreSettings> => {
    const response = await api.get('/api/settings');
    return response.data;
  },

  getStoreInfo: async (): Promise<StoreInfo> => {
    const response = await api.get('/api/settings/store');
    return response.data;
  },

  updateSettings: async (data: StoreSettingsUpdate): Promise<SettingsUpdateResponse> => {
    try {
      const response = await api.patch('/api/settings', data);
      return response.data;
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      // Log the error details for debugging
      if (axios.isAxiosError(error)) {
        console.error('Settings update error:', {
          status: _castErr.response?.status,
          data: _castErr.response?.data,
          message: _castErr.message
        });
      } else {
        console.error('Settings update error:', error);
      }
      throw error;
    }
  },

  updateStoreInfo: async (storeInfo: Partial<StoreInfo>): Promise<StoreInfo> => {
    const response = await api.patch('/api/settings/store', storeInfo);
    return response.data;
  },

  resetToDefault: async (): Promise<StoreSettings> => {
    const response = await api.patch('/api/settings', { resetToDefault: true });
    return response.data;
  },

  // Waste item prices
  getWasteItemPrices: async (): Promise<Record<string, number>> => {
    const response = await api.get('/api/settings/waste-item-prices')
    return response.data
  },
  updateWasteItemPrices: async (prices: Record<string, number>): Promise<Record<string, number>> => {
    const response = await api.patch('/api/settings/waste-item-prices', { prices })
    return response.data
  },

  // Custom waste items
  getCustomWasteItems: async (): Promise<WasteItem[]> => {
    const response = await api.get('/api/settings/custom-waste-items')
    return response.data
  },
  addCustomWasteItem: async (item: Omit<WasteItem, '_id'>): Promise<WasteItem> => {
    const response = await api.post('/api/settings/custom-waste-items', item)
    return response.data
  },
  updateCustomWasteItem: async (itemId: string, item: Partial<WasteItem>): Promise<WasteItem> => {
    const response = await api.patch(`/api/settings/custom-waste-items/${itemId}`, item)
    return response.data
  },
  deleteCustomWasteItem: async (itemId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/settings/custom-waste-items/${itemId}`)
    return response.data
  },

  getStoreUsers: async (): Promise<StoreUser[]> => {
    const response = await api.get('/api/settings/store-users')
    return response.data
  }
};
